import { useState, useEffect, useCallback } from 'react'
import { fetchRemarksList, deleteRemark } from '../api/remarksApi'
import { appToast } from '../../../components/AppToast'
import { appConfirm } from '../../../components/AppConfirmModal'

export default function useRemarksList() {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [isReportsOpen, setIsReportsOpen] = useState(false)
  const [sortBy, setSortBy] = useState('remarkDate')
  const [sortOrder, setSortOrder] = useState('desc')

  // Filters
  const [searchText, setSearchText] = useState('')
  const [searchField, setSearchField] = useState('All')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('2026-2027')
  const [batchFilter, setBatchFilter] = useState('All')
  const [remarkTypeFilter, setRemarkTypeFilter] = useState('All')
  const [fromDate, setFromDate] = useState('')
  const [tillDate, setTillDate] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const filters = {
        searchText,
        searchField,
        company: companyFilter,
        branch: branchFilter,
        course: courseFilter,
        academicYear: academicYearFilter,
        batch: batchFilter,
        remarkType: remarkTypeFilter,
        fromDate,
        tillDate,
      }
      const res = await fetchRemarksList(filters, sortBy, sortOrder)
      setRecords(res.records)
    } catch (e) {
      console.error('Failed to load student remarks', e)
      appToast.error('Failed to load student remarks.')
    } finally {
      setLoading(false)
    }
  }, [
    searchText,
    searchField,
    companyFilter,
    branchFilter,
    courseFilter,
    academicYearFilter,
    batchFilter,
    remarkTypeFilter,
    fromDate,
    tillDate,
    sortBy,
    sortOrder,
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    const handler = () => loadData()
    window.addEventListener('remarks-records-updated', handler)
    return () => window.removeEventListener('remarks-records-updated', handler)
  }, [loadData])

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const handleDelete = async (id) => {
    const confirmed = await appConfirm({
      title: 'Delete Remark',
      message: 'Are you sure you want to permanently delete this remark?',
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteRemark(id)
      appToast.success('Remark deleted successfully.')
      loadData()
    } catch (e) {
      console.error('Failed to delete remark', e)
      appToast.error('Failed to delete remark.')
    }
  }

  const handleSendSms = (record) => {
    appToast.info(`Dispatched remark notification via SMS to parent of ${record.fullName}.`)
  }

  const handleSendWhatsApp = (record) => {
    appToast.info(`Dispatched remark summary via WhatsApp to parent of ${record.fullName}.`)
  }

  const handleExportExcel = () => {
    appToast.success('Exporting student remarks list to Excel...')
  }

  const handleResetFilters = () => {
    setSearchText('')
    setSearchField('All')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setAcademicYearFilter('2026-2027')
    setBatchFilter('All')
    setRemarkTypeFilter('All')
    setFromDate('')
    setTillDate('')
    appToast.info('Filters have been reset.')
  }

  const options = {
    searchFields: [
      { value: 'All', label: 'All Fields' },
      { value: 'Student', label: 'Student Name' },
      { value: 'RollNo', label: 'Roll Number' },
      { value: 'IdCardNo', label: 'ID Card No' },
      { value: 'Remark', label: 'Remark Category' },
      { value: 'Course', label: 'Course' },
      { value: 'Batch', label: 'Batch' },
      { value: 'Branch', label: 'Branch' },
      { value: 'Company', label: 'Company' },
    ],
    companies: [
      { value: 'All', label: 'All Companies' },
      { value: 'Tez Educational Trust', label: 'Tez Educational Trust' },
      { value: 'Tez Educational School', label: 'Tez Educational School' },
      { value: 'Tolani College Of Commerce', label: 'Tolani College Of Commerce' },
    ],
    branches: [
      { value: 'All', label: 'All Branches' },
      { value: 'Dadar', label: 'Dadar' },
      { value: 'Thane', label: 'Thane' },
      { value: 'Andheri', label: 'Andheri' },
      { value: 'Borivali', label: 'Borivali' },
    ],
    courses: [
      { value: 'All', label: 'All Courses' },
      { value: 'CA Foundation', label: 'CA Foundation' },
      { value: 'CS Executive', label: 'CS Executive' },
      { value: 'JEE Advanced', label: 'JEE Advanced' },
      { value: 'NEET UG', label: 'NEET UG' },
    ],
    batches: [
      { value: 'All', label: 'All Batches' },
      { value: 'Batch-A (Morning)', label: 'Batch-A (Morning)' },
      { value: 'Batch-CS-01', label: 'Batch-CS-01' },
      { value: 'Super-30 (Intensive)', label: 'Super-30 (Intensive)' },
      { value: 'Med-Achievers', label: 'Med-Achievers' },
    ],
    remarkTypes: [
      { value: 'All', label: 'All Remark Types' },
      { value: 'Appreciation', label: 'Appreciation & Praise' },
      { value: 'Late Coming', label: 'Late Coming' },
      { value: 'Homework Incomplete', label: 'Homework Incomplete' },
      { value: 'Disciplinary Warning', label: 'Disciplinary Warning' },
      { value: 'Performance Improvement', label: 'Performance Improvement' },
      { value: 'Parent Meeting Required', label: 'Parent Meeting Required' },
    ],
    academicYears: [
      { value: 'All', label: 'All Years' },
      { value: '2026-2027', label: '2026-2027' },
      { value: '2025-2026', label: '2025-2026' },
    ],
  }

  return {
    state: {
      records,
      loading,
      isReportsOpen,
      sortBy,
      sortOrder,
      searchText,
      searchField,
      companyFilter,
      branchFilter,
      courseFilter,
      academicYearFilter,
      batchFilter,
      remarkTypeFilter,
      fromDate,
      tillDate,
      options,
    },
    actions: {
      setIsReportsOpen,
      setSearchText,
      setSearchField,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setAcademicYearFilter,
      setBatchFilter,
      setRemarkTypeFilter,
      setFromDate,
      setTillDate,
      handleSort,
      handleDelete,
      handleSendSms,
      handleSendWhatsApp,
      handleExportExcel,
      handleResetFilters,
    },
  }
}
